// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import PlusCircle from "@insite/mobius/Icons/PlusCircle";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { IconPositioner } from "@steiner/MobiusOverrides/AccordionSection/AccordionSection";
import { PaginationPresentationProps } from "@steiner/MobiusOverrides/Pagination";
import ArrowRight from "@steiner/XDL/Icons/ArrowRight";
import MinusCircle from "@steiner/XDL/Icons/MinusCircle";
import { fontConfig } from "@steiner/XDL/PreStyle/typography";
import { color, ellipsis, hideElementOnPrint, xScreen } from "@steiner/XDL/Utilities";
import { css } from "styled-components";

interface SharedStyles {
    displayRange?: TypographyPresentationProps;
}

interface TableWidgetsSharedStyles {
    loadingWrapper?: InjectableCss;
    loadingText?: TypographyPresentationProps;
    loadingSpinner?: LoadingSpinnerProps;
    container?: InjectableCss;
    headingContainer: TypographyPresentationProps;
    heading?: TypographyPresentationProps;
    rangeAndNavigationContainer?: InjectableCss;
    viewAllLink?: LinkPresentationProps;
    productDescriptionCell?: DataTableCellProps;
    accordionSectionProps?: AccordionSectionPresentationProps;
    cell?: DataTableCellProps;
    cellContentContainer?: InjectableCss;
    cellTitle?: TypographyPresentationProps;
    smallScreen?: SharedStyles;
    notFoundMessage?: TypographyPresentationProps;
    searchAccordionSectionProps?: AccordionSectionPresentationProps;
    pagination?: PaginationPresentationProps;
}

const tableWidgetsSharedStyles: TableWidgetsSharedStyles = {
    loadingWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 20px;
            justify-content: center;
            align-items: center;
            padding: 24px;
        `,
    },
    loadingText: {
        color: "secondary",
    },
    loadingSpinner: {
        size: 40,
    },
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            row-gap: 12px;
        `,
    },
    heading: {
        variant: "h3",
        color: "common.white",
        css: css`
            margin-bottom: 0;
        `,
    },
    rangeAndNavigationContainer: {
        css: css`
            display: flex;
            align-items: center;
            margin-left: auto;
            ${xScreen("lte", 350)} {
                margin-left: initial;
            }
        `,
    },
    viewAllLink: {
        color: "common.white",
        icon: {
            position: "right",
            iconProps: {
                src: ArrowRight,
                color: "common.white",
                size: 16,
                css: css`
                    opacity: 0.66;
                    margin-left: 6px;
                `,
            },
        },
        css: css`
            white-space: nowrap;
            &::after {
                content: "";
                display: inline-block;
                margin: 0 16px;
                border-left: 1px ${color("common.white")} solid;
                width: 1px;
                height: 30px;
            }
            ${hideElementOnPrint}
        `,
    },
    accordionSectionProps: {
        mergeCss: true,
        expandIconProps: {
            src: PlusCircle,
            color: "common.white",
            size: 21,
        },
        collapseIconProps: {
            src: MinusCircle,
            color: "common.white",
            size: 21,
        },
        headerProps: {
            css: css`
                &&& > button {
                    flex-direction: row-reverse;
                    justify-content: flex-end;
                    column-gap: 12px;
                    border: none;
                    align-items: center;

                    ${IconPositioner} {
                        height: 21px;
                        width: 21px;
                        padding: 0;
                        max-height: unset;
                        max-width: unset;
                    }
                    &,
                    &:focus {
                        background-color: ${color("primary")};
                        padding: 12px;
                    }
                }
                ${hideElementOnPrint}
            `,
        },
        panelProps: {
            css: css`
                border: 0;
                padding: 0;
                ${({ hidden }: any) =>
                    !hidden &&
                    css`
                        display: flex;
                        flex-direction: column;
                        row-gap: 12px;
                    `}
            `,
        },
    },
    cell: {
        mergeCss: true,
        css: css`
            padding: 0;
            word-wrap: break-word;
            white-space: normal;
        `,
    },
    headingContainer: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: ${color("primary")};
            padding: 12px;
            width: 100%;
            ${xScreen("lte", 350)} {
                row-gap: 8px;
                flex-wrap: wrap;
            }
        `,
    },
    cellContentContainer: {
        css: css`
            padding: 12px;
            display: grid;
            grid-template-columns: 220px auto;
            row-gap: 14px;
            column-gap: 20px;
            ${xScreen("lte", "xs")} {
                grid-template-columns: 152px auto;
                column-gap: 0;
            }
        `,
    },
    cellTitle: {
        weight: fontConfig.weight.bold,
    },
    notFoundMessage: {},
    searchAccordionSectionProps: {
        mergeCss: true,
        headerProps: {
            css: css`
                &&& > button {
                    padding: 16px;
                    width: auto;
                    column-gap: 16px;
                    background: ${color("common.ui2")};
                    border: none;
                    align-items: center;
                    ${TypographyStyle} {
                        color: ${color("primary")};
                        font-size: 16px;
                    }
                }
            `,
        },
        panelProps: {
            css: css`
                & {
                    border: 1px solid ${color("common.ui2")};
                    padding: 16px;
                }
            `,
        },
    },
    pagination: {
        mergeCss: true,
        cssOverrides: {
            pagination: css`
                justify-content: space-between;
                ${hideElementOnPrint}
            `,
            linkList: css`
                & {
                    margin-left: 0;
                }
            `,
        },
    },
};

export default tableWidgetsSharedStyles;
